
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { useSysInfoStore, useSimTableStore, useResultStore, useCustomCalcResultStore } from '@/shared/service';
import { generateAPI } from '@/shared/lib';
import { getCookieValue } from '@/functions';
// import KokiFloodReleaseSupportInfo from '@/components/rightContents/KokiFloodReleaseSupportInfo.vue';
// import GraphSetting from '../GraphSetting.vue';
// import DamSupport from '../DamSupport.vue';
// import DamTable from '../DamTable.vue';
import RChart from '@/components/molecules/RChart.vue';
import RyuryoChart from '@/components/molecules/RyuryoChart.vue';
import SimTable from '@/components/organisms/SimTable.vue';
import DamSupport from '@/components/organisms/DamSupport.vue';
import DamTable from '@/components/organisms/DamTable.vue';
import DamDischargeInfoTable from '@/components/organisms/DamDischargeInfoTable.vue';
import CustomGraphSetting from './CustomGraphSetting.vue';

@Component({
  components: {
    // GraphSetting,
    DamSupport,
    DamTable,
    DamDischargeInfoTable,
    // KokiFloodReleaseSupportInfo,
    CustomGraphSetting,
    RChart,
    RyuryoChart,
    SimTable,
  },
})
export default class CustomDamPrediction extends Vue {
  @Prop({ type: Function, required: false, default: null })
  onBackToRainfall!: (() => void) | null;

  @Prop({ type: Function, required: false, default: null })
  onMoveToSettingQoutAllConditions!: (() => void) | null;

  @Prop({ type: String, required: false, default: '' })
  calculationFileName!: string;

  @Prop({ type: String, required: true })
  stationId!: string;

  @Prop({ type: Function, required: true })
  setStationId!: (value: string) => void;

  @Prop({ type: Boolean, required: false, default: false })
  isSavedResultMode!: boolean;

  @Prop({ type: String, required: false, default: '' })
  customTargetTime!: string;

  title = '';

  description = '';
  
  /**
   * cookieからユーザー名を取得
   */
  get userName() {
    return getCookieValue('username') || '';
  }

  get defaultTitle() {
    // 現在時刻を取得
    const now = new Date();
    const currentTimeStr = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // 計算対象時刻を取得（保存済み結果モードの場合は選択されたアイテムから、通常モードはcustomTargetTimeから）
    let targetTimeStr = '';
    if (this.isSavedResultMode && this.selectedCalcItem?.tgttime) {
      // "YYYY-MM-DD HH:mm" 形式を "YYYY/MM/DD HH:mm" に変換
      targetTimeStr = this.selectedCalcItem.tgttime.replace(/-/g, '/');
    } else if (this.customTargetTime) {
      // "YYYY-MM-DD HH:mm" 形式を "YYYY/MM/DD HH:mm" に変換
      targetTimeStr = this.customTargetTime.replace(/-/g, '/');
    }
    
    if (targetTimeStr) {
      const userName = this.userName || '';
      return `【${currentTimeStr} ${userName}実行】${targetTimeStr}再現計算`;
    }
    return '';
  }

  isSaving = false;

  isLoadingCalcList = false;

  calcListItems: any[] = [];

  selectedCalcItem: any = null;

  // Store
  customCalcResultStore = useCustomCalcResultStore();
  sysInfoStore = useSysInfoStore();
  simTableStore = useSimTableStore();

  get simTable() {
    const tableData = this.customCalcResultStore.customCalcResult?.simTable ?? null;
    if (
      tableData?.floodControlEffectFlow === undefined
      || tableData?.floodControlEffectWlevel === undefined
      || tableData?.maxFlowDam === undefined
      || tableData?.maxFlowNodam === undefined
      || tableData?.maxWlevelDam === undefined
      || tableData?.maxWlevelNodam === undefined
    ) {
      return null;
    }
    return tableData;
  }

  get dischargeInfo() {
    return this.customCalcResultStore.customCalcResult?.dischargeInfo ?? null;
  }

  get supportInfoList() {
    return this.customCalcResultStore.customCalcResult?.supportInfo ?? [];
  }

  get printMode() {
    return this['$store'].getters.printMode;
  }

  get result() {
    return this.customCalcResultStore.customCalcResult;
  }

  get basinId() {
    return this['$store'].getters.basinId;
  }

  get stationName() {
    const { stationsMap } = this['$store'].getters;
    if (!stationsMap || !this.stationId) {
      return '';
    }
    return stationsMap[this.stationId].name;
  }

  get type() {
    return this['$store'].getters.type;
  }

  get figure() {
    return this.sysInfoStore.stationsMap[this.stationId]?.figure ?? '';
  }

  /**
   * 対象時刻を "YYYY-MM-DD HH:mm" 形式で取得
   */
  get targetTimeFormatted() {
    let timeValue = '';
    
    // 保存済み結果モードの場合は選択されたアイテムのtgttimeを使用
    if (this.isSavedResultMode && this.selectedCalcItem?.tgttime) {
      timeValue = this.selectedCalcItem.tgttime;
    } else if (this.customTargetTime) {
      // 通常モードの場合はcustomTargetTimeを使用
      timeValue = this.customTargetTime;
    } else {
      // フォールバック: storeから取得
      timeValue = this['$store'].getters.targetTime;
    }
    
    if (!timeValue) {
      return '';
    }
    
    // すでに "YYYY-MM-DD HH:mm" 形式の場合はそのまま返す
    if (timeValue.includes('-') && timeValue.includes(':')) {
      return timeValue;
    }
    
    // "YYYYMMDDHHmm" 形式の場合は "YYYY-MM-DD HH:mm" に変換
    if (timeValue.length === 12 && /^\d{12}$/.test(timeValue)) {
      return `${timeValue.slice(0, 4)}-${timeValue.slice(4, 6)}-${timeValue.slice(6, 8)} ${timeValue.slice(8, 10)}:${timeValue.slice(10, 12)}`;
    }
    
    // その他の形式の場合は空文字
    return '';
  }

  /**
   * 表示用にフォーマットした対象時刻（日本語形式）
   */
  get displayTargetTime() {
    const targetTime = this.targetTimeFormatted;
    if (!targetTime) {
      return '';
    }
    
    // "YYYY-MM-DD HH:mm" 形式を "YYYYMMDDHHmm" 形式に変換
    const formatTargetTime = targetTime.replace(/[-:\s]/g, '');
    
    return `${formatTargetTime.slice(0, 4)}年${formatTargetTime.slice(4, 6)}月${formatTargetTime.slice(6, 8)}日`
        + ` ${formatTargetTime.slice(8, 10)}時${formatTargetTime.slice(10, 12)}分`;
  }

  get canSave() {
    return this.title.trim() !== '' && this.calculationFileName !== '';
  }

  get currentFileName() {
    // 保存済み結果モードの場合は選択されたファイル名を使用
    if (this.isSavedResultMode && this.selectedCalcItem) {
      return this.selectedCalcItem.fileName;
    }
    // 通常モードの場合は計算ファイル名を使用
    return this.calculationFileName;
  }

  get currentTargetTime() {
    // 保存済み結果モードの場合は選択されたアイテムのtgttimeを使用
    if (this.isSavedResultMode && this.selectedCalcItem?.tgttime) {
      return this.selectedCalcItem.tgttime;
    }
    // 通常モードの場合はcustomTargetTimeを使用
    return this.customTargetTime;
  }

  async mounted() {
    // タイトルの初期値を設定
    if (!this.title && !this.isSavedResultMode) {
      this.title = this.defaultTitle;
    }
    
    if (this.isSavedResultMode) {
      await this.loadCalcList();
    }
  }

  @Watch('isSavedResultMode')
  async onSavedResultModeChanged(newValue: boolean) {
    if (newValue) {
      await this.loadCalcList();
    }
  }

  async loadCalcList() {
    try {
      this.isLoadingCalcList = true;
      const response = await generateAPI().customCalcApi.getCalcListApiApiCustomCalcCalclistGet();
      // 逆順にソート（新しいものが上）
      this.calcListItems = (response.calcList || []).reverse();
      
      // 最初のアイテムを自動選択
      if (this.calcListItems.length > 0) {
        [this.selectedCalcItem] = this.calcListItems;
        this.onSelectCalcItem();
      }
    } catch (error) {
      console.error('CALCLIST取得エラー:', error);
      alert('保存済み結果の読み込みに失敗しました。');
    } finally {
      this.isLoadingCalcList = false;
    }
  }

  onSelectCalcItem() {
    if (!this.selectedCalcItem) {
      return;
    }
    
    // 選択されたアイテムのfileNameで結果を取得
    const { fileName } = this.selectedCalcItem;
    this.customCalcResultStore.fetch(this.basinId, this.stationId, fileName);
  }

  @Watch('stationId')
  onStationIdChanged() {
    if (this.isSavedResultMode && this.selectedCalcItem) {
      const { fileName } = this.selectedCalcItem;
      this.customCalcResultStore.fetch(this.basinId, this.stationId, fileName);
    }
  }

  @Watch('customTargetTime')
  onCustomTargetTimeChanged() {
    // 通常モードでタイトルが空または既定値の場合、新しい値で更新
    if (!this.isSavedResultMode && this.defaultTitle) {
      this.title = this.defaultTitle;
    }
  }

  async saveCalculationResult() {
    if (!this.canSave || this.isSaving) {
      return;
    }

    try {
      this.isSaving = true;

      const response = await generateAPI().customCalcApi.addCalcListApiApiCustomCalcCalclistPost({
        addCalcListRequest: {
          fileName: this.calculationFileName,
          title: this.title.trim(),
          description: this.description.trim(),
          tgttime: this.customTargetTime,
        },
      });

      console.log('計算結果保存完了:', response);

      if (response.success) {
        // 成功メッセージを表示
        alert('計算結果を保存しました。');
      } else {
        alert(`保存に失敗しました: ${response.message}`);
      }
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存中にエラーが発生しました。');
    } finally {
      this.isSaving = false;
    }
  }
}
